import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, Users, CheckCircle } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import Calendar from '../core/Calendar/Calendar';
import KPICard from '../core/KPICard/KPICard';
import SessionQuickViewModal from '../common/SessionQuickViewModal';
import apiService from '../../services/api';
import './MentorCalendar.scss';

interface Session {
  id: string;
  title: string;
  description?: string;
  scheduled_date: string;
  scheduled_time?: string;
  duration_minutes: number | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  mentee_first_name?: string;
  mentee_last_name?: string;
  meeting_link?: string;
  location?: string;
  mentee_id: string;
}

const MentorCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [initialMonth, setInitialMonth] = useState<number | undefined>(undefined);
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    completed: 0,
    thisMonth: 0,
  });

  useEffect(() => {
    // Leer el mes desde la URL
    const monthParam = searchParams.get('month');
    if (monthParam) {
      const month = parseInt(monthParam);
      if (month >= 1 && month <= 12) {
        setInitialMonth(month);
      }
    }
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/mentor/sessions');
      const sessionsData = Array.isArray(response.data.data)
        ? response.data.data
        : response.data.data?.sessions || [];

      setSessions(sessionsData);
      calculateStats(sessionsData);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (sessionsData: Session[]) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const thisMonthSessions = sessionsData.filter(s => {
      const sessionDate = new Date(s.scheduled_date);
      return sessionDate.getMonth() === currentMonth &&
             sessionDate.getFullYear() === currentYear;
    });

    setStats({
      total: sessionsData.length,
      scheduled: sessionsData.filter(s => s.status === 'scheduled').length,
      completed: sessionsData.filter(s => s.status === 'completed').length,
      thisMonth: thisMonthSessions.length,
    });
  };

  const convertSessionsToEvents = () => {
    return sessions.map(session => ({
      id: session.id,
      title: session.mentee_first_name && session.mentee_last_name
        ? `${session.mentee_first_name} ${session.mentee_last_name}`
        : session.title,
      date: new Date(session.scheduled_date),
      startTime: session.scheduled_time,
      endTime: session.scheduled_time && session.duration_minutes
        ? calculateEndTime(session.scheduled_time, session.duration_minutes)
        : undefined,
      type: session.status,
    }));
  };

  const calculateEndTime = (startTime: string, durationMinutes: number) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const handleEventClick = (event: any) => {
    const session = sessions.find(s => s.id === event.id);
    if (session) {
      setSelectedSession(session);
    }
  };

  const handleViewDetails = () => {
    if (selectedSession) {
      navigate(`/mentor/sessions/${selectedSession.id}`);
      setSelectedSession(null);
    }
  };

  const handleDateClick = (date: Date) => {
    console.log('Date clicked:', date);
    // Podrías implementar un modal para crear una nueva sesión en esta fecha
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="mentor-calendar">
          <div className="loading-state">
            <div className="spinner" />
            <p>Cargando calendario...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mentor-calendar">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header__info">
            <h1 className="page-header__title">
              <CalendarIcon size={32} />
              Calendario de Sesiones
            </h1>
            <p className="page-header__subtitle">
              Vista mensual de todas tus sesiones de mentoría
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <KPICard
            label="Total Sesiones"
            value={stats.total.toString()}
            icon={<CalendarIcon size={24} />}
            iconColor="navy"
          />
          <KPICard
            label="Programadas"
            value={stats.scheduled.toString()}
            icon={<Clock size={24} />}
            iconColor="navy"
          />
          <KPICard
            label="Completadas"
            value={stats.completed.toString()}
            icon={<CheckCircle size={24} />}
            iconColor="green"
          />
          <KPICard
            label="Este Mes"
            value={stats.thisMonth.toString()}
            icon={<Users size={24} />}
            iconColor="gold"
          />
        </div>

        {/* Calendar */}
        <Calendar
          events={convertSessionsToEvents()}
          onEventClick={handleEventClick}
          onDateClick={handleDateClick}
          initialMonth={initialMonth}
        />

        {/* Quick View Modal */}
        {selectedSession && (
          <SessionQuickViewModal
            session={selectedSession}
            onClose={() => setSelectedSession(null)}
            onViewDetails={handleViewDetails}
            role="mentor"
          />
        )}
      </div>
    </MainLayout>
  );
};

export default MentorCalendar;
